// The Linear team and project pickers (poc CT-009, module-14 Section 2), shared by registration's Linear step
// (S8) and App settings > Linear (S9). The options are read live from Linear: the public teams the tool's app
// token can see, then the chosen team's projects. Changing the team clears the project.
import type { LinearProject, LinearTargetInput, LinearTeam } from '@feedback/shared/contracts/linear';
import { useQuery } from '@tanstack/react-query';
import { linearErrorText, linearProjectsQuery, linearTeamsQuery, teamLabel } from '../lib/linear-api';
import { Button, Field, Notice, Select } from '../ui/primitives';

export type LinearTargetErrors = { teamId?: string | undefined; projectId?: string | undefined };

export const EMPTY_LINEAR_TARGET: LinearTargetInput = { teamId: '', projectId: '' };

/** Both are required: the messages to show next to the empty ones (an empty object when both are chosen). */
export function linearTargetErrors(value: LinearTargetInput): LinearTargetErrors {
  return {
    ...(value.teamId ? {} : { teamId: 'Choose a Linear team' }),
    ...(value.projectId ? {} : { projectId: 'Choose a Linear project' }),
  };
}

export function hasLinearTargetErrors(errors: LinearTargetErrors): boolean {
  return Boolean(errors.teamId ?? errors.projectId);
}

/** The chosen team and project as Linear names them (from the pickers' cached reads), once `enabled`. */
export function useLinearChoice(
  value: LinearTargetInput,
  enabled: boolean,
): { team?: LinearTeam; project?: LinearProject } {
  const teams = useQuery({ ...linearTeamsQuery, enabled });
  const projects = useQuery({ ...linearProjectsQuery(value.teamId), enabled: enabled && value.teamId !== '' });
  const team = teams.data?.find((t) => t.id === value.teamId);
  const project = projects.data?.find((p) => p.id === value.projectId);
  return { ...(team ? { team } : {}), ...(project ? { project } : {}) };
}

export function LinearTargetFields({
  value,
  onChange,
  errors = {},
}: {
  value: LinearTargetInput;
  onChange: (value: LinearTargetInput) => void;
  errors?: LinearTargetErrors;
}) {
  const teams = useQuery(linearTeamsQuery);
  const projects = useQuery({ ...linearProjectsQuery(value.teamId), enabled: value.teamId !== '' });
  const failed = teams.isError ? teams : projects.isError ? projects : null;
  const projectPrompt = !value.teamId
    ? 'Choose a team first'
    : projects.isPending
      ? 'Loading projects…'
      : 'Choose a project…';

  return (
    <div className="flex flex-col gap-4">
      <Field label="Linear team" required hint="The public Linear teams the tool can see." error={errors.teamId}>
        {({ id, describedBy, invalid }) => (
          <Select
            id={id}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            value={value.teamId}
            disabled={!teams.data}
            onChange={(e) => onChange({ teamId: e.target.value, projectId: '' })}
          >
            <option value="">{teams.isPending ? 'Loading teams…' : 'Choose a team…'}</option>
            {teams.data?.map((t) => (
              <option key={t.id} value={t.id}>
                {teamLabel(t)}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label="Linear project" required hint="The team’s projects in Linear." error={errors.projectId}>
        {({ id, describedBy, invalid }) => (
          <Select
            id={id}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            value={value.projectId}
            disabled={!value.teamId || !projects.data}
            onChange={(e) => onChange({ ...value, projectId: e.target.value })}
          >
            <option value="">{projectPrompt}</option>
            {(value.teamId ? (projects.data ?? []) : []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {teams.data?.length === 0 ? (
        <Notice tone="warning" title="No Linear teams to choose from">
          Linear shows the tool no public teams. Ask a Linear admin to add the tool’s app to a public team.
        </Notice>
      ) : null}
      {value.teamId && projects.data?.length === 0 ? (
        <Notice tone="warning" title="This team has no projects">
          Create a project for the app in Linear, then try again.
          <div className="mt-2">
            <Button size="sm" onClick={() => void projects.refetch()} busy={projects.isFetching}>
              Try again
            </Button>
          </div>
        </Notice>
      ) : null}
      {failed ? (
        <Notice tone="danger" title="Linear’s teams and projects couldn’t be loaded">
          {linearErrorText(failed.error)}
          <div className="mt-2">
            <Button size="sm" onClick={() => void failed.refetch()} busy={failed.isFetching}>
              Try again
            </Button>
          </div>
        </Notice>
      ) : null}
    </div>
  );
}
